import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableHeader,
  TooltipProvider,
  cn,
} from '@shigo-ui/ui';

import { DASHBOARD_TABLE_HEADER_HEIGHT_PX } from './DashboardDataTable.constants';
import { type DashboardDataTableProps } from './DashboardDataTable.types';
import { useDashboardDataTableStickyColumns } from './useDashboardDataTableStickyColumns';
import { DashboardDataTableHeader } from './DashboardDataTableHeader';
import { DashboardDataTableFooter } from './DashboardDataTablePagination';
import { DashboardDataTableFillHeightScroll } from './DashboardDataTableFillHeightScroll';
import { DashboardDataTableRows } from './DashboardDataTableRows';
import { DashboardDataTableTableFooter } from './DashboardDataTableTableFooter';
import { dashboardPageTitleClassName } from './DashboardTypography.styles';

export { DashboardTableDateCell, DashboardTableStatusBadge } from './DashboardTableCells';
export { DashboardTableFilterTrigger } from './DashboardTableFilterTrigger';
export { DashboardTableMultiSelectFilter } from './DashboardTableMultiSelectFilter';
export { DashboardTableSearchInput } from './DashboardTableSearchInput';

export function DashboardDataTable<TItem>({
  title,
  tableLabel,
  className,
  surfaceClassName,
  toolbar,
  filterToolbar,
  cardTitle,
  cardActions,
  columns: inputColumns,
  items,
  getRowKey,
  loading = false,
  errorMessage,
  emptyMessage = 'No records found.',
  loadingMessage = 'Loading...',
  pagination,
  sort,
  footerSummary,
  footerRows,
  minWidthClassName = 'min-w-[720px]',
  fillHeight = false,
  fillEmptyHeight = false,
  maxBodyHeight,
  rowLink,
}: DashboardDataTableProps<TItem>) {
  const pinHeader = fillHeight || maxBodyHeight !== undefined;
  const stretchMessage =
    fillEmptyHeight && !pinHeader && !loading && (Boolean(errorMessage) || items.length === 0);
  const scrollMaxHeightPx =
    maxBodyHeight === undefined ? undefined : DASHBOARD_TABLE_HEADER_HEIGHT_PX + maxBodyHeight;
  const { columns, tableRef } = useDashboardDataTableStickyColumns(
    inputColumns,
    sort,
    minWidthClassName,
    pinHeader,
  );
  const columnCount = Math.max(columns.length, 1);
  const showHeader = Boolean(title || toolbar || filterToolbar);
  const hasCardHeader = Boolean(cardTitle ?? cardActions);

  const table = (
    <Table
      ref={tableRef}
      aria-busy={loading}
      className={cn(
        // One compact density for every dashboard table: 13px values in ~38px rows.
        'table-fixed text-[13px]',
        // Card-header tables size columns to their content, so wide values and column names
        // never spill or get cut; the table scrolls horizontally when it does not fit.
        hasCardHeader && 'table-auto [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap',
        // A grown table hands its extra height to the percentage-height message cell.
        stretchMessage && 'flex-1 [&_[data-slot=dashboard-table-message]]:h-full',
        minWidthClassName,
      )}
      containerClassName={cn(
        pinHeader && 'overflow-visible',
        stretchMessage && 'flex flex-1 flex-col',
      )}
      aria-label={tableLabel ?? (typeof title === 'string' ? title : undefined)}
    >
      <TableHeader>
        <DashboardDataTableHeader
          columns={columns}
          sort={sort}
          pinHeader={pinHeader}
          tinted={hasCardHeader}
        />
      </TableHeader>
      <TableBody>
        <DashboardDataTableRows
          columnCount={columnCount}
          columns={columns}
          emptyMessage={emptyMessage}
          errorMessage={errorMessage}
          getRowKey={getRowKey}
          items={items}
          loading={loading}
          rowLink={rowLink}
        />
      </TableBody>
      {footerRows?.length ? (
        <DashboardDataTableTableFooter columns={columns} footerRows={footerRows} />
      ) : null}
    </Table>
  );

  return (
    <section
      className={cn(
        'mx-auto flex w-full max-w-[1920px] min-w-0 flex-col gap-6',
        fillHeight ? 'h-full min-h-0' : maxBodyHeight === undefined && 'min-h-full',
        stretchMessage && 'flex-1',
        className,
      )}
    >
      {showHeader ? (
        <header className="flex shrink-0 flex-col gap-4">
          {title || toolbar ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              {title ? (
                <div className="min-w-0">
                  <h1 className={dashboardPageTitleClassName()}>{title}</h1>
                </div>
              ) : (
                <span aria-hidden="true" />
              )}
              {toolbar ? <div className="flex flex-wrap items-center gap-2">{toolbar}</div> : null}
            </div>
          ) : null}
          {filterToolbar ? <div className="w-full min-w-0">{filterToolbar}</div> : null}
        </header>
      ) : null}

      <Card
        className={cn(
          'border-border bg-card min-w-0 overflow-hidden rounded-md shadow-[var(--shadow-xs)]',
          (fillHeight || stretchMessage) && 'flex min-h-0 flex-1 flex-col',
          surfaceClassName,
        )}
      >
        <CardContent
          className={cn(
            'relative p-0',
            (fillHeight || stretchMessage) && 'flex min-h-0 flex-1 flex-col',
          )}
        >
          {hasCardHeader ? (
            <div
              data-slot="dashboard-table-card-header"
              className="border-border flex shrink-0 flex-wrap items-center gap-2.5 border-b px-4 py-3"
            >
              {cardTitle ? <h2 className="text-base font-semibold">{cardTitle}</h2> : null}
              {cardActions ? (
                <div className="ml-auto flex flex-wrap items-center gap-2">{cardActions}</div>
              ) : null}
            </div>
          ) : null}
          <TooltipProvider>
            {fillHeight || scrollMaxHeightPx !== undefined ? (
              <DashboardDataTableFillHeightScroll maxHeightPx={scrollMaxHeightPx}>
                {table}
              </DashboardDataTableFillHeightScroll>
            ) : (
              <div
                className={cn('w-full overflow-x-auto', stretchMessage && 'flex flex-1 flex-col')}
              >
                {table}
              </div>
            )}
          </TooltipProvider>

          {loading ? (
            // The skeleton rows show progress; the message is for assistive tech only.
            <span
              role="status"
              aria-live="polite"
              aria-label={typeof loadingMessage === 'string' ? loadingMessage : 'Loading'}
              className="sr-only"
            >
              {loadingMessage}
            </span>
          ) : null}
          <DashboardDataTableFooter
            footerSummary={footerSummary}
            pagination={pagination}
            className={pinHeader ? 'shrink-0' : undefined}
          />
        </CardContent>
      </Card>
    </section>
  );
}
